import {
  FILTER_KEYS,
  MODEL_FILTER_KEYS
} from '../components/ModelFilter/constants';

/**
 * @typedef {Object} ModelFilterItem
 * @property {number} id
 * @property {number} parentId
 * @property {string} title
 * @property {string} value
 */

export function isValidModelFilterData(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  if (data?.brand?.[0]?.id && !data?.type?.[0]?.id) return false;
  if (data?.model?.[0]?.id && !data?.brand?.[0]?.id) return false;
  return (
    (data.type === undefined || isValidFilterItemArray(data.type)) &&
    (data.brand === undefined || isValidFilterItemArray(data.brand)) &&
    (data.model === undefined || isValidFilterItemArray(data.model))
  );
}

function isValidFilterItemArray(arr) {
  if (arr === null) return true; // null is allowed
  if (!Array.isArray(arr)) return false;
  return arr.every(
    item =>
      typeof item === 'object' &&
      item !== null &&
      !Array.isArray(item) &&
      typeof item.id === 'string' &&
      typeof item.text === 'string'
  );
}

/**
 *
 * @param {ModelFilterItem[]} modelFilterItems
 * @returns {Object[]} A tree structure representing the model filter items
 * @description This function takes an array of model filter items and constructs a tree structure.
 * Each item can have a parentId, and the function organizes them into a nested structure.
 */
export function makeModelFilterTree(modelFilterItems) {
  if (!Array.isArray(modelFilterItems) || modelFilterItems.length === 0) {
    return [];
  }
  const roots = modelFilterItems.filter(item => !item.parentId);

  const map = new Map();
  modelFilterItems.forEach(item => {
    map.set(item.id, { ...item, children: [] });
  });

  modelFilterItems.forEach(item => {
    const parent = map.get(item.parentId);
    if (parent) {
      parent.children.push(map.get(item.id));
    }
  });

  return roots.map(root => map.get(root.id));
}

/**
 * @typedef {ModelFilterItem[]} TableRow
 */

/**
 * @param {ModelFilterItem[]} modelFilterItems
 * @returns {TableRow[]} An array of table rows, each representing a path from root to leaf
 * @description This function converts model filter items into a format suitable for a table,
 * where each leaf node is a row in the table, with columns representing the path from root to leaf.
 */
export function makeModelFilterTable(modelFilterItems) {
  if (!Array.isArray(modelFilterItems) || modelFilterItems.length === 0) {
    return [];
  }

  // Create a map for quick access to items by ID
  const itemMap = new Map();
  modelFilterItems.forEach(item => {
    itemMap.set(item.id, item);
  });

  // Build a map of parent to children
  const parentToChildren = new Map();
  modelFilterItems.forEach(item => {
    if (item.parentId) {
      const children = parentToChildren.get(item.parentId) || [];
      children.push(item.id);
      parentToChildren.set(item.parentId, children);
    }
  });

  // Identify leaf nodes (items that don't have any children)
  const leafIds = modelFilterItems
    .filter(item => !parentToChildren.has(item.id))
    .map(item => item.id);

  // Build paths from root to leaf
  const tableRows = [];
  for (const leafId of leafIds) {
    const path = [];
    let currentId = leafId;

    // Build path from leaf to root
    while (currentId) {
      const item = itemMap.get(currentId);
      path.unshift(item);
      currentId = item.parentId;
    }

    tableRows.push(path);
  }

  // Sort alphabetically: first by TYP, then by MÄRKE, then by MODELL
  tableRows.sort((a, b) => {
    // Compare TYP (index 0)
    const typeA = a[0]?.value || '';
    const typeB = b[0]?.value || '';
    const typeCompare = typeA.localeCompare(typeB);
    if (typeCompare !== 0) return typeCompare;

    // Compare MÄRKE (index 1)
    const brandA = a[1]?.value || '';
    const brandB = b[1]?.value || '';
    const brandCompare = brandA.localeCompare(brandB);
    if (brandCompare !== 0) return brandCompare;

    // Compare MODELL (index 2)
    const modelA = a[2]?.value || '';
    const modelB = b[2]?.value || '';
    return modelA.localeCompare(modelB);
  });

  return tableRows;
}

/**
 * @param {{
 *   id: string,
 *   name: string,
 *   __typename: 'MultiListFilter',
 *   isActive: boolean,
 *   lists: Object[]
 * }} data
 * @return {{ type: Object[] | null, brand: Object[] | null, model: Object[] | null }}
 */
export function parseModelFilterData(data) {
  if (!data || !data.lists || !Array.isArray(data.lists)) {
    return {
      type: null,
      brand: null,
      model: null
    };
  }

  const result = {
    type: null,
    brand: null,
    model: null
  };

  for (const list of data.lists) {
    const dataName = MODEL_FILTER_KEYS[list.name];
    if (!dataName) continue;

    const value = list.hasActiveItems
      ? list.items.flatMap(item =>
          item.isActive ? [{ id: item.id, text: item.text }] : []
        )
      : null;

    result[dataName] = value;
  }

  return result;
}

/**
 * Compares two model filter data objects by checking the ID of the first item
 * for each filter key.
 * @param {object | null | undefined} a - First data object.
 * @param {object | null | undefined} b - Second data object.
 * @returns {boolean} - True if they are considered the same.
 */
export function isSameModelFilterData(a, b) {
  if (!a || !b) {
    return a === b;
  }
  return FILTER_KEYS.every(key => {
    const aId = a[key]?.[0]?.id;
    const bId = b[key]?.[0]?.id;
    return aId === bId;
  });
}

/**
 * Checks if the filter data object has any selected values.
 * @param {object | null | undefined} data - The data object to check.
 * @returns {boolean} - True if data is valid and has at least one filter value.
 */
export function hasData(data) {
  return !!(
    isValidModelFilterData(data) &&
    (data.type || data.brand || data.model)
  );
}

/**
 * Checks if type, brand and model is not null.
 * @param {object | null | undefined} data - The data object to check.
 * @returns {boolean} - True if data is valid and has at least one filter value.
 */
export function isFullySelected(data) {
  return !!(
    isValidModelFilterData(data) &&
    data.type !== null &&
    data.brand !== null &&
    data.model !== null
  );
}
